export const LOCALE = "es-PR";

const money = new Intl.NumberFormat(LOCALE, { style: "currency", currency: "USD" });
const moneyWhole = new Intl.NumberFormat(LOCALE, {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});
const percent1 = new Intl.NumberFormat(LOCALE, { style: "percent", maximumFractionDigits: 1 });
const MINUS = "−";

export const MONTHS_SHORT = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
export const MONTHS_LONG = [
  "enero",
  "febrero",
  "marzo",
  "abril",
  "mayo",
  "junio",
  "julio",
  "agosto",
  "septiembre",
  "octubre",
  "noviembre",
  "diciembre",
];

export function formatMoney(value: number | null | undefined, opts: { cents?: boolean } = {}): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  const formatter = opts.cents === false ? moneyWhole : money;
  const text = formatter.format(Math.abs(value));
  return value < 0 ? `${MINUS}${text}` : text;
}

/** $950 · $10.7K · $1.2M (Intl's compact notation inserts a space we do not want). */
export function formatCompactMoney(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  const abs = Math.abs(value);
  const sign = value < 0 ? MINUS : "";
  if (abs >= 1_000_000) return `${sign}$${trimZero((abs / 1_000_000).toFixed(1))}M`;
  if (abs >= 1_000) return `${sign}$${trimZero((abs / 1_000).toFixed(1))}K`;
  return `${sign}${moneyWhole.format(abs)}`;
}

const trimZero = (s: string) => s.replace(/\.0$/, "");

export function formatPercent(ratio: number | null | undefined, opts: { signed?: boolean } = {}): string {
  if (ratio === null || ratio === undefined || !Number.isFinite(ratio)) return "—";
  const text = percent1.format(Math.abs(ratio));
  if (ratio < 0) return `${MINUS}${text}`;
  return opts.signed && ratio > 0 ? `+${text}` : text;
}

export function formatSignedMoney(value: number): string {
  const text = money.format(Math.abs(value));
  if (value < 0) return `${MINUS}${text}`;
  return value > 0 ? `+${text}` : text;
}

/** Upstream sign convention: positive = money out, negative = money in. */
export function isInflow(amount: number): boolean {
  return amount < 0;
}

export function formatTxnAmount(amount: number): { text: string; tone: "in" | "out" | "zero" } {
  if (amount === 0) return { text: money.format(0), tone: "zero" };
  const text = money.format(Math.abs(amount));
  return isInflow(amount) ? { text: `+${text}`, tone: "in" } : { text: `${MINUS}${text}`, tone: "out" };
}

/** Parse `YYYY-MM-DD` as a local date (never `new Date("YYYY-MM-DD")`, which is UTC). */
export function parseYmd(ymd: string): Date {
  const [y, m, d] = ymd.split("-").map(Number);
  return new Date(y!, (m ?? 1) - 1, d ?? 1);
}

export function toYmd(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/** "12 sep 2026" */
export function formatDate(ymd: string | null | undefined): string {
  if (!ymd) return "—";
  const d = parseYmd(ymd);
  return `${d.getDate()} ${MONTHS_SHORT[d.getMonth()]} ${d.getFullYear()}`;
}

/** "12 de septiembre de 2026" (optionally with weekday). */
export function formatDateLong(input: string | Date, opts: { weekday?: boolean } = {}): string {
  const d = typeof input === "string" ? parseYmd(input) : input;
  return new Intl.DateTimeFormat(LOCALE, {
    day: "numeric",
    month: "long",
    year: "numeric",
    ...(opts.weekday ? { weekday: "long" } : {}),
  }).format(d);
}

/** ISO timestamp → "12 sep 2026, 10:14" in the viewer's local time. */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  const time = new Intl.DateTimeFormat(LOCALE, { hour: "2-digit", minute: "2-digit", hour12: false }).format(d);
  return `${d.getDate()} ${MONTHS_SHORT[d.getMonth()]} ${d.getFullYear()}, ${time}`;
}

/** "septiembre 2026" */
export function formatMonth(ym: string): string {
  const [y, m] = ym.split("-").map(Number);
  return `${MONTHS_LONG[(m ?? 1) - 1]} ${y}`;
}

/** Axis tick: "sep" (es-PR would give "sept"). */
export function monthTick(ym: string): string {
  return MONTHS_SHORT[Number(ym.slice(5, 7)) - 1] ?? ym;
}

const rtf = new Intl.RelativeTimeFormat(LOCALE, { numeric: "auto" });

/** "hace 3 horas", "ayer", "hace 2 días" */
export function formatRelative(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return "nunca";
  const diffSeconds = Math.round((new Date(iso).getTime() - now.getTime()) / 1000);
  const abs = Math.abs(diffSeconds);
  if (abs < 60) return "hace un momento";
  if (abs < 3600) return rtf.format(Math.round(diffSeconds / 60), "minute");
  if (abs < 86_400) return rtf.format(Math.round(diffSeconds / 3600), "hour");
  if (abs < 30 * 86_400) return rtf.format(Math.round(diffSeconds / 86_400), "day");
  return rtf.format(Math.round(diffSeconds / (30 * 86_400)), "month");
}

export function currentMonth(now: Date = new Date()): string {
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

export function shiftMonth(ym: string, n: number): string {
  const [y, m] = ym.split("-").map(Number);
  const d = new Date(y!, (m ?? 1) - 1 + n, 1);
  return currentMonth(d);
}

export function monthRange(ym: string): { from: string; to: string } {
  const [y, m] = ym.split("-").map(Number);
  const last = new Date(y!, m ?? 1, 0).getDate();
  return { from: `${ym}-01`, to: `${ym}-${String(last).padStart(2, "0")}` };
}

export function isValidMonth(value: string | null | undefined): value is string {
  return !!value && /^\d{4}-(0[1-9]|1[0-2])$/.test(value);
}

/** "en 3 días" / "hoy" / "hace 2 días" for a business date relative to today. */
export function formatDaysUntil(ymd: string, today: Date = new Date()): string {
  const target = parseYmd(ymd);
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const days = Math.round((target.getTime() - start.getTime()) / 86_400_000);
  if (days === 0) return "hoy";
  if (days === 1) return "mañana";
  if (days === -1) return "ayer";
  return rtf.format(days, "day");
}
